import React from 'react';
import PrintDocument, { DocSection, DocFieldGrid, DocField, formatDate } from '../PrintDocument';
import { sectionLabel, personName, formatRegNo } from '../labels';
import './documents.css';

/**
 * StudentRecordDoc — وثيقة طالب.
 *
 * The formal record handed to a parent or to a government office: who
 * the pupil is, who their parents are, which civil registry they are
 * entered in, and which class they currently sit in. It is the paper
 * that answers "prove this child attends your school".
 *
 * WHY THE FIELDS ARE LAID OUT THIS WAY
 * A record like this is verified field-by-field against an identity
 * document, so the identity block (name, father, mother, national id,
 * registry place + number, birth date + place) comes first and stays
 * together, then enrolment, then contact. A missing field prints a
 * dash rather than disappearing — see DocField: a dropped
 * "الرقم الوطني" row reads as "this school does not record it", a
 * dashed one reads as "not on file for this pupil", and only the
 * second is true.
 *
 * PROPS CONTRACT
 *   student   object   REQUIRED. A row from `api.student(id)` (route
 *                      people.js getStudent: every `students` column
 *                      plus section_id / section_label / grade_title /
 *                      sibling_count). A row from
 *                      `api.studentDirectory()` also works — it carries
 *                      the same identity fields plus the family ones —
 *                      but it does NOT carry registry_place /
 *                      registry_number / birth_place / registration_date,
 *                      so a formal record should be built from
 *                      api.student(id).
 *                      Fields read:
 *                        name, first_name, last_name, grandfather_name,
 *                        gender, birth_date, birth_place, national_id,
 *                        id_document_type, registry_place,
 *                        registry_number, registration_number,
 *                        registration_date, status, student_phone,
 *                        family_status, health_status,
 *                        displacement_status, origin_place, notes,
 *                        section_id, section_label, grade_title
 *   family    object?  A row from `api.family(student.family_id)` (or
 *                      the family fields already merged onto a
 *                      studentDirectory row). Fields read:
 *                        father_name, father_phone, father_job,
 *                        mother_name, mother_last_name, mother_phone,
 *                        last_name, address, amana
 *                      Omit it and the family block prints dashes.
 *   academicYear string? printed as the document subtitle ("للعام الدراسي …")
 *   purpose   string?  what the document is issued for ("لتقديمه إلى …").
 *                      Printed in the closing sentence when given.
 *   issuedAt  string?  ISO date; defaults to today.
 *   docNumber string|number? reference number shown in the letterhead.
 *                      Pass the student's registration_number or an
 *                      office serial — this component invents nothing.
 *   logoUrl   string?  school logo; a placeholder box prints without it.
 *   principalName string? printed under the principal's signature line.
 *   showToolbar / onClose / autoPrint — forwarded to PrintDocument.
 *
 * PURE / PRESENTATIONAL: fetches nothing, owns no state. The page
 * loads student + family and passes them in.
 */
export default function StudentRecordDoc({
  student,
  family,
  academicYear,
  purpose,
  issuedAt,
  docNumber,
  logoUrl,
  principalName,
  showToolbar = true,
  onClose,
  autoPrint = false,
}) {
  if (!student) return null;

  const fam = family || student; // directory rows already carry the family fields
  const name = personName(student);

  // sectionLabel() is the ONLY way a section name may be produced: the
  // bare title ("أولى م") names twelve different sections in the live
  // data, so composing it here would put an ambiguous class on a
  // formal document.
  const section = student.section_label || student.section_id
    ? sectionLabel(student)
    : null;

  const gender = student.gender === 'male' ? 'ذكر'
    : student.gender === 'female' ? 'أنثى'
      : null;

  const statusText = student.status === 'active' ? 'نشط'
    : student.status === 'inactive' ? 'منفكّ'
      : null;

  return (
    <PrintDocument
      title="وثيقة طالب"
      subtitle={academicYear ? `للعام الدراسي ${academicYear}` : undefined}
      docNumber={docNumber ?? (formatRegNo(student.registration_number) || undefined)}
      issuedAt={issuedAt}
      logoUrl={logoUrl}
      schoolSubtitle="شؤون الطلاب"
      showToolbar={showToolbar}
      onClose={onClose}
      autoPrint={autoPrint}
      footerNote="هذه الوثيقة صادرة عن إدارة المدرسة ولا يُعتدّ بها ما لم تكن مختومة وموقّعة."
      signatures={[
        { title: 'أمين السجل' },
        { title: 'مدير المدرسة', name: principalName || undefined },
      ]}
    >
      {/* The attestation sentence. This — not the grid — is what an
          office stamps; the grid below is the supporting detail. */}
      <p className="ui-docx-statement">
        تشهد إدارة المدرسة بأن الطالب <strong>{name}</strong>
        {section ? <> مسجّل لديها في <strong>{section}</strong></> : ' مسجّل لديها'}
        {statusText ? <>، وهو {statusText}</> : ''}
        {purpose ? <>، وقد أُعطيت له هذه الوثيقة بناءً على طلبه {purpose}</>
          : '، وقد أُعطيت له هذه الوثيقة بناءً على طلبه دون أدنى مسؤولية على الإدارة'}.
      </p>

      <DocSection title="بيانات الطالب">
        <div className="ui-docx-record-top">
          <DocFieldGrid columns={2}>
            <DocField label="الاسم الكامل" value={name} wide />
            <DocField label="الاسم الأول" value={student.first_name} />
            <DocField label="اسم الجد" value={student.grandfather_name} />
            <DocField label="الكنية" value={student.last_name || fam.last_name} />
            <DocField label="الجنس" value={gender} />
            <DocField label="تاريخ الميلاد" value={formatDate(student.birth_date)} numeric />
            <DocField label="مكان الميلاد" value={student.birth_place} />
            <DocField label="الرقم الوطني" value={student.national_id} numeric />
            <DocField label="وثائق ثبوتية" value={student.id_document_type} />
            <DocField label="محل القيد" value={student.registry_place} />
            <DocField label="رقم القيد" value={student.registry_number} numeric />
          </DocFieldGrid>

          {/* Photo slot: an <img> when one is stored, otherwise a
              bordered box of the same size, so the record can be
              completed by hand at the counter and the layout never
              shifts once pictures are uploaded. */}
          {student.picture_url
            ? <img className="ui-docx-photo" src={student.picture_url} alt="" />
            : <div className="ui-docx-photo" aria-hidden="true">صورة الطالب</div>}
        </div>
      </DocSection>

      <DocSection title="البيانات الدراسية">
        <DocFieldGrid columns={2}>
          <DocField label="الصف" value={student.grade_title} />
          <DocField label="الشعبة" value={section} />
          <DocField label="رقم التسجيل" value={formatRegNo(student.registration_number)} numeric />
          <DocField label="تاريخ التسجيل" value={formatDate(student.registration_date)} numeric />
          <DocField label="الحالة" value={statusText} />
          <DocField label="الوضع الصحي" value={student.health_status} />
        </DocFieldGrid>
      </DocSection>

      <DocSection title="بيانات الأسرة">
        <DocFieldGrid columns={2}>
          <DocField label="اسم الأب" value={fam.father_name} />
          <DocField label="مهنة الأب" value={fam.father_job} />
          <DocField label="اسم الأم" value={fam.mother_name} />
          <DocField label="كنية الأم" value={fam.mother_last_name} />
          <DocField label="الحالة العائلية" value={student.family_status} />
          <DocField label="وثائق ثبوتية دراسية" value={student.displacement_status} />
          <DocField label="وافد من" value={student.origin_place} />
        </DocFieldGrid>
      </DocSection>

      <DocSection title="بيانات الاتصال">
        <DocFieldGrid columns={2}>
          <DocField label="هاتف الأب" value={fam.father_phone} numeric />
          <DocField label="هاتف الأم" value={fam.mother_phone} numeric />
          <DocField label="هاتف الطالب" value={student.student_phone} numeric />
          <DocField label="الأمانة" value={fam.amana} />
          <DocField label="العنوان" value={fam.address} wide />
        </DocFieldGrid>
      </DocSection>

      {student.notes && (
        <DocSection title="ملاحظات">
          <p className="ui-docx-statement">{student.notes}</p>
        </DocSection>
      )}
    </PrintDocument>
  );
}
